import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyImage - Progressive image loading with blur-up effect
 * 
 * Features:
 * - Intersection Observer for lazy loading
 * - Blur-up placeholder effect
 * - Smooth fade-in transition
 * - Error handling with fallback
 * - Optional low-quality placeholder (LQIP)
 */
const LazyImage = ({
  src,
  alt = '',
  className = '',
  placeholderColor = '#1f2937',
  blurAmount = 20,
  transitionDuration = 500,
  threshold = 0.1,
  rootMargin = '50px',
  fallbackSrc = null,
  aspectRatio = null,
  onClick,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Set up Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  // Load image when in view
  useEffect(() => {
    if (!isInView || !src) return;

    const img = new Image();
    img.src = src;

    img.onload = () => {
      setCurrentSrc(src);
      setIsLoaded(true);
      setHasError(false);
    };

    img.onerror = () => {
      setHasError(true);
      if (fallbackSrc) {
        setCurrentSrc(fallbackSrc);
        setIsLoaded(true);
      }
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [isInView, src, fallbackSrc]);

  // Generate a simple gradient placeholder based on the image URL
  const generatePlaceholderGradient = () => {
    if (!src) return placeholderColor;
    
    // Create a hash from the src to generate consistent colors
    let hash = 0;
    for (let i = 0; i < src.length; i++) {
      hash = src.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const hue1 = Math.abs(hash % 360);
    const hue2 = (hue1 + 40) % 360;
    
    return `linear-gradient(135deg, hsl(${hue1}, 30%, 20%) 0%, hsl(${hue2}, 30%, 15%) 100%)`;
  };

  const containerStyle = {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: placeholderColor,
    background: generatePlaceholderGradient(),
    ...(aspectRatio && { aspectRatio }),
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: isLoaded ? 1 : 0,
    filter: isLoaded ? 'blur(0px)' : `blur(${blurAmount}px)`,
    transform: isLoaded ? 'scale(1)' : 'scale(1.1)',
    transition: `opacity ${transitionDuration}ms ease-out, filter ${transitionDuration}ms ease-out, transform ${transitionDuration}ms ease-out`,
  };

  const placeholderStyle = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: isLoaded ? 0 : 1,
    transition: `opacity ${transitionDuration}ms ease-out`,
    pointerEvents: 'none',
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={containerStyle}
      onClick={onClick}
      {...props}
    >
      {/* Placeholder with shimmer */}
      <div style={placeholderStyle}>
        <div 
          className="absolute inset-0 animate-pulse"
          style={{ background: generatePlaceholderGradient() }}
        />
        {/* Optional loading indicator */}
        {isInView && !isLoaded && !hasError && (
          <div className="relative z-10">
            <div className="w-8 h-8 border-2 border-gray-600 border-t-yellow-400 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Actual image */}
      {currentSrc && (
        <img
          ref={imgRef}
          src={currentSrc}
          alt={alt}
          style={imageStyle}
          loading="lazy"
          decoding="async"
        />
      )}

      {/* Error state */}
      {hasError && !fallbackSrc && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gray-800/80"
          style={{ opacity: 1 }}
        >
          <div className="text-center text-gray-500">
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">Image unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * LazyBackgroundImage - For background images with blur-up effect
 */
export const LazyBackgroundImage = ({
  src,
  className = '',
  children,
  placeholderColor = '#1f2937',
  blurAmount = 30,
  transitionDuration = 700,
  overlay = false,
  overlayColor = 'rgba(0,0,0,0.5)',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView || !src) return;

    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);

    return () => { img.onload = null; };
  }, [isInView, src]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundColor: placeholderColor }}
      {...props}
    >
      {/* Background image with blur-up effect */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: isInView ? `url(${src})` : 'none',
          filter: isLoaded ? 'blur(0px)' : `blur(${blurAmount}px)`,
          transform: isLoaded ? 'scale(1)' : 'scale(1.1)',
          opacity: isLoaded ? 1 : 0.5,
          transition: `filter ${transitionDuration}ms ease-out, transform ${transitionDuration}ms ease-out, opacity ${transitionDuration}ms ease-out`,
        }}
      />

      {/* Optional overlay */}
      {overlay && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: overlayColor }}
        />
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

/**
 * LazyAvatar - Circular avatar with lazy loading
 */
export const LazyAvatar = ({
  src,
  alt = '',
  size = 40,
  className = '',
  fallbackInitials = '?',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
    img.onerror = () => setHasError(true);

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return (
    <div
      className={`relative overflow-hidden rounded-full bg-gray-700 flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      {...props}
    >
      {/* Fallback initials */}
      {(!isLoaded || hasError) && (
        <span 
          className="text-gray-400 font-medium"
          style={{ fontSize: size * 0.4 }}
        >
          {fallbackInitials}
        </span>
      )}

      {/* Avatar image */}
      {src && !hasError && (
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          style={{ opacity: isLoaded ? 1 : 0 }}
          loading="lazy"
        />
      )}
    </div>
  );
};

export default LazyImage;
